/**
 * Shared rules for the two patient-search surfaces — the Request Detail
 * screen's Adjust Search panel and the dedicated Search Patient Records screen.
 * Both hit the same endpoint with the same page size, the same "at least two
 * criteria" gate and the same field validation; keeping one copy here means a
 * staff member can't get different results from the two screens for the same
 * criteria.
 *
 * Every rule below mirrors the API's `BoltPatientSearchCriteriaRequestValidator`
 * one-for-one. Where the two drift, the staff member gets a 400 with a message
 * written for an API consumer instead of an inline hint on the field that is
 * wrong, so the mirror is the point — not belt-and-braces.
 */

import { SortDirectionType } from '../../core/models/sort-direction';
import { isNumericDate, numericDateToIso } from '../../shared/utils/date-time/date-time.utils';
import { PatientSearchRequest } from '../services/patient-portal-user-requests.service';

/**
 * Records requested per search. Neither surface has a paging UI, so both report
 * the server's total alongside the page rather than presenting one page as the
 * complete match set.
 */
export const PATIENT_SEARCH_PAGE_SIZE = 25;

/**
 * Minimum populated criteria before a search may run. One field (a bare last
 * name) would return most of the patient population to a PHI screen.
 */
export const MIN_SEARCH_FIELDS = 2;

/**
 * Criteria as the staff member typed them: trimmed, `null` when left empty.
 *
 * These five are the whole criteria set the search endpoint accepts — there is
 * deliberately no phone number here. The API's search body has no phone field
 * and its matcher never filters on one, so a phone box would read as a working
 * criterion while changing nothing (and, worse, would count toward the
 * two-criteria gate the server also enforces).
 */
export interface PatientSearchCriteria {
  firstName: string | null;
  lastName: string | null;
  /** `MM/DD/YYYY` as typed — converted to the API's `yyyy-MM-dd` on send. */
  dateOfBirth: string | null;
  lastFiveSsn: string | null;
  mrn: string | null;
}

/**
 * Shortest value accepted in a free-text criterion — first name, last name or
 * MRN. Mirrors the API's `SearchRequestMinimumStringLength`: these are matched
 * with a leading-wildcard LIKE, so anything shorter turns the search into a full
 * scan of the patient table, and the API rejects it with a 400.
 */
export const MIN_SEARCH_FIELD_LENGTH = 3;

/** Longest first/last name the API accepts (`PatientConstraints.*NameMaximumLength`). */
export const MAX_SEARCH_NAME_LENGTH = 125;

/** Longest MRN the API accepts (`MedicalRecordNumberMaximumLength`). */
export const MAX_SEARCH_MRN_LENGTH = 20;

/** The SSN criterion is the last five digits (`LastFiveSocialSecurityNumberMaximumLength`). */
export const MAX_SEARCH_SSN_LENGTH = 5;

/**
 * True when a typed free-text criterion (first name, last name, MRN) is
 * populated but shorter than {@link MIN_SEARCH_FIELD_LENGTH}. An empty box is
 * not "invalid" — it's a criterion left unset, which {@link hasEnoughCriteria}
 * covers instead. Like the date rule, this blocks the search with an inline
 * error rather than dropping the field, so the search never quietly widens past
 * what was asked for.
 */
export function isSearchFieldTooShort(value: string): boolean {
  const trimmed = value.trim();
  return trimmed !== '' && trimmed.length < MIN_SEARCH_FIELD_LENGTH;
}

/**
 * True when a typed SSN criterion holds anything but digits. The inputs carry
 * `maxlength` (the length rule) and `inputmode="numeric"`, but `inputmode` is
 * only a soft-keyboard hint — it does nothing on a desktop keyboard and nothing
 * against a paste, so `12-34` would otherwise reach the API and come back as a
 * 400 rather than an inline hint on the field.
 */
export function isSearchSsnInvalid(value: string): boolean {
  const trimmed = value.trim();
  return trimmed !== '' && !/^\d+$/.test(trimmed);
}

/**
 * True when a typed date-of-birth isn't a real `MM/DD/YYYY` calendar date. An
 * empty field is not "invalid" — it's simply one of the criteria left unset.
 * Both forms block the search on this rather than dropping the field, because a
 * dropped field silently broadens a PHI search beyond what was asked for.
 */
export function isSearchDateOfBirthInvalid(value: string): boolean {
  const trimmed = value.trim();
  return trimmed !== '' && !isNumericDate(trimmed);
}

/**
 * True when at least {@link MIN_SEARCH_FIELDS} criteria are populated. Counts
 * exactly the fields the server's own two-criteria rule counts — a UI gate that
 * counted anything extra would let a search through that the API then rejects.
 */
export function hasEnoughCriteria(criteria: PatientSearchCriteria): boolean {
  return (
    [
      criteria.firstName,
      criteria.lastName,
      criteria.dateOfBirth,
      criteria.lastFiveSsn,
      criteria.mrn,
    ].filter(Boolean).length >= MIN_SEARCH_FIELDS
  );
}

/**
 * Build the API request body for a patient search. The date of birth goes
 * through `numericDateToIso`, which yields `null` for anything that isn't a
 * real date — callers gate on {@link isSearchDateOfBirthInvalid} first so a
 * mistyped date surfaces as an inline error instead of quietly widening the
 * search.
 *
 * The acting admin user is deliberately absent: the API resolves it from the
 * access token, so sending it would imply the client picks who the search is
 * audited against.
 */
export function buildPatientSearchRequest(
  patientPortalUserRequestId: number,
  criteria: PatientSearchCriteria,
): PatientSearchRequest {
  return {
    patientPortalUserRequestId,
    firstName: criteria.firstName,
    lastName: criteria.lastName,
    dateOfBirth: numericDateToIso(criteria.dateOfBirth),
    lastFiveSocialSecurityNumber: criteria.lastFiveSsn,
    medicalRecordNumber: criteria.mrn,
    sortDirection: SortDirectionType.Ascending,
    sortColumn: 'DisplayName',
    pageNumber: 1,
    pageSize: PATIENT_SEARCH_PAGE_SIZE,
  };
}
